import type { Lot } from '../data/mockLots';
import { formatStartsIn, useCountdown } from '../hooks/useCountdown';
import { clickableProps } from '../lib/a11y';
import { revealProps } from '../lib/motion';
import { resolveFinish, resolveRarity } from '../lib/rarity';
import { SkinFrame } from './SkinFrame';
import { SkinThumbnail } from './SkinThumbnail';

// A miniature of the canonical LotHero: same skin-over-image hero + price strip
// composition, scaled down for the horizontal "Starting soon" rail. The live
// RingTimer medallion is replaced by a bare neon countdown (no depleting arc to
// show pre-start), CURRENT PRICE by START PRICE, and the LAST BIDDERS cluster by
// the market reference.
export function StartingSoonCard({
  lot,
  index,
  onSelect,
}: {
  lot: Lot & { startsAt: number };
  index: number;
  onSelect?: () => void;
}) {
  const startsIn = useCountdown(lot.startsAt);
  const rarity = resolveRarity(lot);
  const finish = resolveFinish(lot);

  return (
    <article
      {...clickableProps(onSelect)}
      {...revealProps(index, {
        base: 200,
        step: 50,
        className: `relative w-[180px] shrink-0 overflow-hidden border border-edge bg-bg-elev/60 ${onSelect ? 'cursor-pointer hover:border-edge-bright transition-colors' : ''}`,
      })}
    >
      <div className="relative h-[120px] overflow-hidden">
        <div className="absolute inset-0">
          {lot.imageUrl ? (
            <SkinThumbnail
              imageUrl={lot.imageUrl}
              alt={`${lot.weaponLabel} | ${lot.skinName}`}
              weapon={lot.weapon}
              palette={lot.palette}
              rarityColor={lot.rarityColor ?? null}
              size="sm"
            />
          ) : (
            <SkinFrame weapon={lot.weapon} palette={lot.palette} size="sm" />
          )}
        </div>

        <div className="absolute inset-0 pointer-events-none scanlines mix-blend-overlay opacity-40" />

        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(10,14,20,0.4) 0%, rgba(10,14,20,0.02) 32%, rgba(10,14,20,0.94) 100%)',
          }}
        />

        <span
          className="absolute top-1.5 right-2 font-mono font-semibold text-[14px] tabular-nums leading-none text-primary"
          style={{ textShadow: '0 0 10px rgba(0,229,255,0.55), 0 1px 3px rgba(5,8,16,0.9)' }}
        >
          {formatStartsIn(startsIn)}
        </span>

        <div className="absolute inset-x-0 bottom-0 px-2.5 pb-2">
          {finish && (
            <span
              className="font-mono font-bold text-[8px] tracking-[0.18em] uppercase block leading-none mb-1"
              style={{ color: finish.color, textShadow: `0 0 8px ${finish.color}73` }}
            >
              {finish.label}
            </span>
          )}
          <h4 className="font-display font-bold text-[15px] leading-[0.95] tracking-tight text-fg truncate">
            {lot.weaponLabel}
          </h4>
          <h5
            className="font-display font-medium text-[11px] leading-tight truncate mt-0.5"
            style={{ color: lot.palette.accent ?? lot.palette.to }}
          >
            | {lot.skinName}
          </h5>
          <div className="flex items-center gap-1.5 font-mono text-[8px] leading-none tracking-[0.16em] mt-1.5 min-w-0">
            <span
              aria-hidden
              className="inline-block w-[5px] h-[5px] rounded-full shrink-0"
              style={{ background: rarity.color, boxShadow: `0 0 6px ${rarity.color}` }}
            />
            <span className="truncate" style={{ color: rarity.color }}>
              {rarity.label}
            </span>
            <span className="text-fg-dim">·</span>
            <span className="text-fg/75 shrink-0">{lot.wear}</span>
          </div>
        </div>
      </div>

      <div className="relative bg-bg-elev/95 border-t border-edge px-2.5 py-2">
        <div className="absolute -top-px left-2.5 right-2.5 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
        {/* 2-row grid: labels share the top line, values share the bottom line.
            A flex row with items-end would bottom-align the values but leave the
            taller START PRICE column's label floating above MARKET's. */}
        <div className="grid grid-cols-[1fr_auto] items-end gap-x-2 gap-y-1">
          <span className="font-mono text-[8px] tracking-[0.24em] text-fg-muted">START PRICE</span>
          <span className="font-mono text-[8px] tracking-[0.24em] text-fg-muted justify-self-end">
            MARKET
          </span>
          <div className="flex items-baseline gap-1 min-w-0">
            <span className="font-mono font-bold text-[17px] tabular-nums leading-none text-fg">
              $0.00
            </span>
            <span className="font-mono text-[9px] tabular-nums text-danger leading-none">
              +$0.01
            </span>
          </div>
          <span className="font-mono font-semibold text-[12px] tabular-nums leading-none justify-self-end text-fg/90">
            ${Math.round(lot.marketPrice).toLocaleString('en-US')}
          </span>
        </div>
      </div>
    </article>
  );
}
